/* =============================================================================
 *  KOBHO LABS · Tabla comparativa (web IT)
 *  GFWebs · Gilbert Fernández
 *
 *  Progressive enhancement sobre la tabla que emite el custom liquid.
 *  Sin dependencias. Idempotente: se puede llamar tantas veces como haga falta
 *  (útil en Shopify, donde las secciones se re-renderizan en el editor de temas).
 *
 *  Qué hace:
 *    1. Normaliza las celdas:
 *         · envuelve el contenido de cada `<td>` en `.tc-cell`, la caja de
 *           ancho ajustado con la que el CSS decide si el valor va centrado
 *           (contenido corto) o alineado a la izquierda (contenido largo);
 *         · envuelve cada par «icono + texto» en `.tc-item` para que el icono
 *           quede con sangría francesa aunque el liquid emita los nodos sueltos.
 *       Es solo un respaldo: si el liquid ya emite `.tc-cell`, aquí no se toca
 *       nada. La alineación no la decide este archivo, la decide el CSS.
 *    2. Detecta si la tabla desborda y marca el wrapper con `tc-has-overflow`,
 *       `tc-no-overflow`, `tc-at-start`, `tc-at-end` y `tc-scrolled`, que el CSS
 *       usa para las sombras de la columna fija, el degradado derecho, el aviso
 *       de deslizar y para desactivar el scroll (y su rebote) cuando no hace falta.
 *       Publica además `--tc-fade-top` y `--tc-fade-height` con la caja real de
 *       la tarjeta, para que el degradado no se salga por arriba ni por abajo.
 *    3. Añade el aviso «Desliza para ver la comparativa» en móvil.
 *    4. Hace el contenedor accesible por teclado.
 *
 *  API pública:  window.KobhoTablaComparativa.init(root)
 * ========================================================================== */

(function (window, document) {
  'use strict';

  var WRAPPER   = '.tabla-comparativa-wrapper';
  var SCROLLER  = '.tabla-comparativa-scroll';
  var FLAG      = 'tcInit';           // dataset flag para no inicializar dos veces
  var EDGE      = 2;                  // tolerancia en px para los extremos

  // Textos: los aporta la sección liquid desde los locales del tema
  // (data-tc-hint / data-tc-label). Los valores de aquí son solo el respaldo
  // para cuando la tabla se use fuera de Shopify.
  var HINT_TEXT  = 'Desliza para ver la comparativa';
  var LABEL_TEXT = 'Tabla comparativa desplazable';

  // ---------------------------------------------------------------------------
  //  Utilidades
  // ---------------------------------------------------------------------------

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function isIcon(node) {
    return node &&
           node.nodeType === 1 &&
           (node.classList.contains('icono-check') ||
            node.classList.contains('icono-cross'));
  }

  function throttleRaf(fn) {
    var ticking = false;
    return function () {
      if (ticking) { return; }
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        fn();
      });
    };
  }

  // ---------------------------------------------------------------------------
  //  1. Normalización de celdas
  //
  //  El liquid emite, dentro de un <td>:
  //      27,95 €
  //      <span class="icono-check">✓</span> Sí
  //      <span class="icono-check">✓</span> No dependencia
  //
  //  a) `.tc-cell`: caja que envuelve todo el contenido de la celda. El CSS le
  //     da `max-width: max-content` + `margin-inline: auto`, y de ahí sale la
  //     regla del diseño: si el contenido cabe en una línea la caja se encoge y
  //     queda centrada; si no cabe, ocupa la celda entera y el texto queda a la
  //     izquierda. Aquí no se mide nada ni se cuentan caracteres: solo se crea
  //     la caja si el liquid no la ha emitido.
  //  b) `.tc-item`: sin envoltura, con textos de dos o más líneas el icono no
  //     se alinea. Agrupamos cada icono con el texto que lo sigue.
  // ---------------------------------------------------------------------------

  // Envuelve el contenido del `<td>` en `.tc-cell`. Si ya viene del liquid, la
  // reutiliza tal cual. Idempotente.
  function ensureCellBox(cell) {
    var children = toArray(cell.children);
    var i;

    for (i = 0; i < children.length; i += 1) {
      if (children[i].classList.contains('tc-cell')) { return children[i]; }
    }

    var box = document.createElement('div');
    box.className = 'tc-cell';
    while (cell.firstChild) { box.appendChild(cell.firstChild); }
    cell.appendChild(box);
    return box;
  }

  function normalizeCell(cell) {
    if (cell.querySelector('.tc-item')) { return; }

    var nodes = toArray(cell.childNodes);
    var hasIcon = nodes.some(isIcon);
    if (!hasIcon) { return; }

    var groups  = [];
    var current = null;

    nodes.forEach(function (node) {
      if (isIcon(node)) {
        current = [node];
        groups.push(current);
        return;
      }

      // Texto o marcado que acompaña al icono anterior.
      if (current) {
        current.push(node);
        return;
      }

      // Contenido anterior al primer icono: se deja donde está.
      groups.push([node]);
    });

    var fragment = document.createDocumentFragment();

    groups.forEach(function (group) {
      if (!isIcon(group[0])) {
        // Trozo suelto sin icono: solo lo reinsertamos si tiene contenido.
        group.forEach(function (node) {
          if (node.nodeType === 3 && !node.textContent.trim()) { return; }
          fragment.appendChild(node);
        });
        return;
      }

      var item = document.createElement('span');
      item.className = 'tc-item';

      group.forEach(function (node) {
        // Recortamos el espacio sobrante entre el icono y su texto.
        if (node.nodeType === 3) {
          var text = node.textContent.replace(/^\s+|\s+$/g, ' ');
          if (!text.trim()) { return; }
          node.textContent = text;
        }
        item.appendChild(node);
      });

      fragment.appendChild(item);
    });

    cell.innerHTML = '';
    cell.appendChild(fragment);
  }

  function normalizeTable(wrapper) {
    toArray(wrapper.querySelectorAll('.tabla-comparativa tbody td'))
      .forEach(function (cell) {
        normalizeCell(ensureCellBox(cell));
      });
  }

  // ---------------------------------------------------------------------------
  //  2. Estado de scroll
  // ---------------------------------------------------------------------------

  function createHint(wrapper) {
    if (wrapper.querySelector('.tc-scroll-hint')) { return; }

    var hint = document.createElement('p');
    hint.className = 'tc-scroll-hint';
    hint.setAttribute('aria-hidden', 'true');

    var label = document.createElement('span');
    label.textContent = wrapper.dataset.tcHint || HINT_TEXT;

    var arrow = document.createElement('span');
    arrow.className = 'tc-scroll-hint__arrow';
    arrow.textContent = '\u2192';

    hint.appendChild(label);
    hint.appendChild(arrow);
    wrapper.appendChild(hint);
  }

  // El degradado de la derecha es un ::after del wrapper, pero debe cubrir solo
  // la tarjeta: el wrapper también contiene la leyenda (arriba) y el aviso de
  // deslizar (abajo). Publicamos la caja real del scroller en dos custom
  // properties para que el CSS lo posicione y lo recorte a la altura correcta.
  function syncFadeBox(wrapper, scroller) {
    var wrapperTop = wrapper.getBoundingClientRect().top;
    var box = scroller.getBoundingClientRect();
    var top    = Math.round(box.top - wrapperTop) + 'px';
    var height = Math.round(box.height) + 'px';

    if (wrapper.style.getPropertyValue('--tc-fade-top') !== top) {
      wrapper.style.setProperty('--tc-fade-top', top);
    }
    if (wrapper.style.getPropertyValue('--tc-fade-height') !== height) {
      wrapper.style.setProperty('--tc-fade-height', height);
    }
  }

  function syncScrollState(wrapper, scroller) {
    syncFadeBox(wrapper, scroller);

    var overflow = scroller.scrollWidth - scroller.clientWidth;
    var hasOverflow = overflow > EDGE;

    wrapper.classList.toggle('tc-has-overflow', hasOverflow);
    // `tc-no-overflow` apaga el scroll por CSS cuando la tabla cabe entera:
    // un contenedor sin scroll no puede rebotar. Es una clase propia (y no
    // `:not(.tc-has-overflow)`) para que, si el JS no llega a ejecutarse, el
    // contenedor siga siendo desplazable y no se recorte la tabla.
    wrapper.classList.toggle('tc-no-overflow', !hasOverflow);

    if (!hasOverflow) {
      wrapper.classList.add('tc-at-start', 'tc-at-end');
      wrapper.classList.remove('tc-scrolled');
      scroller.removeAttribute('tabindex');
      scroller.removeAttribute('role');
      // Si veníamos de un estado desplazado, devolvemos la tabla al origen
      // antes de bloquear el scroll: si no, quedaría recortada por la izquierda.
      if (scroller.scrollLeft !== 0) { scroller.scrollLeft = 0; }
      return;
    }

    // Accesibilidad: solo tiene sentido enfocar el contenedor si se desplaza.
    if (!scroller.hasAttribute('tabindex')) {
      scroller.setAttribute('tabindex', '0');
      scroller.setAttribute('role', 'region');
      if (!scroller.hasAttribute('aria-label')) {
        scroller.setAttribute('aria-label', wrapper.dataset.tcLabel || LABEL_TEXT);
      }
    }

    var left = scroller.scrollLeft;
    wrapper.classList.toggle('tc-at-start', left <= EDGE);
    wrapper.classList.toggle('tc-at-end',   left >= overflow - EDGE);
    if (left > EDGE) { wrapper.classList.add('tc-scrolled'); }
  }

  // ---------------------------------------------------------------------------
  //  3. Inicialización de una tabla
  // ---------------------------------------------------------------------------

  function initWrapper(wrapper) {
    if (wrapper.dataset[FLAG] === '1') { return; }

    var scroller = wrapper.querySelector(SCROLLER);
    if (!scroller) { return; }

    wrapper.dataset[FLAG] = '1';

    normalizeTable(wrapper);
    createHint(wrapper);

    var sync = throttleRaf(function () {
      syncScrollState(wrapper, scroller);
    });

    scroller.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    if (typeof window.ResizeObserver === 'function') {
      new window.ResizeObserver(sync).observe(scroller);
    }

    // Las fuentes web cambian el ancho de la tabla al cargar.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(sync).catch(function () { /* no pasa nada */ });
    }

    syncScrollState(wrapper, scroller);
  }

  // ---------------------------------------------------------------------------
  //  4. Arranque
  // ---------------------------------------------------------------------------

  function init(root) {
    var scope = root || document;
    toArray(scope.querySelectorAll(WRAPPER)).forEach(initWrapper);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Shopify: el editor de temas re-renderiza secciones sin recargar la página.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  window.KobhoTablaComparativa = { init: init };

}(window, document));
